import { useMutation } from '@apollo/client';
import { changeUserLanguageMutation } from 'components/LanguageSwitch';
import { ChangeUserLanguage, ChangeUserLanguageVariables } from 'graphql/types';
import i18n from 'i18n';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { BehaviorSubject, Subject } from 'rxjs';

export function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useObservableState<T>(initialValue: T) {
  const ref = useRef<BehaviorSubject<T>>();
  if (ref.current == null) {
    ref.current = new BehaviorSubject(initialValue);
  }
  const [state, setState] = useState(initialValue);

  useEffect(() => {
    const s = ref.current!.subscribe({
      next(value) {
        setState(value);
      },
    });
    return () => {
      s.unsubscribe();
    };
  }, []);

  return [state, ref.current!] as const;
}

export function useSubject<T>() {
  const ref = useRef<Subject<T>>();
  if (ref.current == null) {
    ref.current = new Subject<T>();
  }
  return ref.current!;
}

export function useChangeUserLanguage(onCompleted?: () => void) {
  const resetStore = (onCompleted?: () => void) => {
    client.resetStore().then(() => {
      // follow up code should be executed after the resetStore promise is resolved
      if (onCompleted) {
        onCompleted();
      }
    });
  };

  const [changeLanguage, { client }] = useMutation<
    ChangeUserLanguage,
    ChangeUserLanguageVariables
  >(changeUserLanguageMutation, {
    onCompleted: async (data) => {
      // Sync users language with i18next
      const userLanguage = data.changeUserLanguage?.language;
      if (userLanguage != null && userLanguage !== i18n.language) {
        await i18n.changeLanguage(userLanguage);
      }

      resetStore(onCompleted);
      window.location.reload();
    },
  });

  return [changeLanguage];
}

export const useDebouncedValue = <T>(value: T, ms: number): T => {
  const [stateValue, setStateValue] = useState<T>();
  useDebounce(() => setStateValue(value), ms, [value]);

  return stateValue ?? value;
};
